import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, Section, Field, InfoNote } from "../components/legal/LegalLayout";
import { legalConfig } from "../data/legalConfig";

export default function ConditionsGenerales() {
  return (
    <LegalLayout
      title="Conditions générales"
      description="Conditions générales de vente et de service de Louis-Melchior Danse — Tarifs, réservation, annulation, paiement, rétractation."
      testId="page-conditions-generales"
      breadcrumbPath="/conditions-generales"
    >
      <p className="text-base mb-8">
        Les présentes conditions générales (ci-après « CG ») régissent les prestations de cours de danse proposées par {legalConfig.publisherFullName}, exerçant sous le nom {legalConfig.brandName} (SIREN {legalConfig.siren}). Toute réservation implique l'acceptation des présentes CG.
      </p>

      <Section title="1. Identité du prestataire" testId="cg-prestataire">
        <Field label="Prestataire" value={legalConfig.publisherFullName} />
        <Field label="Nom commercial" value={legalConfig.brandName} />
        <Field label="Forme" value={legalConfig.legalForm} />
        <Field label="Adresse" value={`${legalConfig.legalAddress.line1}, ${legalConfig.legalAddress.postalCode} ${legalConfig.legalAddress.city}`} />
        <Field label="SIREN" value={legalConfig.siren} />
        <Field label="SIRET" value={legalConfig.siret} />
        <Field label="APE" value={legalConfig.apeCode} />
        <Field label="Email" value={legalConfig.contactEmail} />
        <Field label="Téléphone" value={legalConfig.contactPhone} />
      </Section>

      <Section title="2. Champ d'application" testId="cg-champ">
        <p>
          Les présentes CG s'appliquent à toute prestation d'enseignement de la danse fournie par {legalConfig.brandName} en France et au Luxembourg, qu'il s'agisse de cours particuliers, cours collectifs, préparation d'ouverture de bal, animations ou stages.
        </p>
      </Section>

      <Section title="3. Prestations proposées" testId="cg-prestations">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#C9A869]">
          <li>cours particuliers (individuel ou couple), à l'unité ou en pack ;</li>
          <li>cours collectifs (groupes constitués, événements) ;</li>
          <li>préparation d'ouverture de bal de mariage ;</li>
          <li>animations en résidences seniors ou structures partenaires ;</li>
          <li>stages et ateliers ponctuels ;</li>
          <li>cours à domicile, en salle louée ou en lieu public selon le projet.</li>
        </ul>
      </Section>

      <Section title="4. Tarifs" testId="cg-tarifs">
        <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-[#C9A869]">
          <li>Cours à l'unité (individuel ou couple) : {legalConfig.pricing.hourly}.</li>
          <li>{legalConfig.pricing.pack5}.</li>
          <li>{legalConfig.pricing.pack10}.</li>
          <li>{legalConfig.pricing.travel}.</li>
        </ul>
        <p className="mt-3">
          Les tarifs sont exprimés en euros, toutes taxes éventuelles comprises. <strong className="text-[#C9A869]">{legalConfig.vatMention}</strong>.
        </p>
        <p>
          Les tarifs des prestations sur-mesure (mariages, animations B2B, cours collectifs) font l'objet d'un devis personnalisé, valable 30 jours.
        </p>
      </Section>

      <Section title="5. Réservation" testId="cg-reservation">
        <p>
          La réservation s'effectue par échange écrit (email, formulaire) ou téléphonique. La date, le lieu, la durée et le tarif sont confirmés par {legalConfig.brandName} avant le cours. La réservation devient ferme à compter de cette confirmation.
        </p>
      </Section>

      <Section title="6. Paiement" testId="cg-paiement">
        <p>
          Le paiement est dû le jour du cours pour les cours à l'unité, ou avant le premier cours pour les packs. Modes acceptés : virement bancaire, espèces, ou tout autre moyen convenu à l'avance. Une facture peut être émise sur demande.
        </p>
      </Section>

      <Section title="7. Annulation et report par le client" testId="cg-annulation">
        <p>
          Toute annulation ou demande de report doit être signalée au moins <strong className="text-[#C9A869]">24 heures avant</strong> l'heure prévue du cours. Au-delà de ce délai, ou en cas d'absence non signalée, le cours est considéré comme dû et peut être facturé ou décompté du pack.
        </p>
        <p>
          En cas de force majeure dûment justifiée, un report sans frais sera proposé.
        </p>
      </Section>

      <Section title="8. Retard" testId="cg-retard">
        <p>
          Le cours se termine à l'heure initialement convenue, quelle que soit l'heure d'arrivée du client. Le créneau ne peut être prolongé sans accord préalable.
        </p>
      </Section>

      <Section title="9. Annulation par le prestataire" testId="cg-annulation-prestataire">
        <p>
          En cas d'empêchement, {legalConfig.brandName} s'engage à prévenir le client dans les meilleurs délais et à proposer un report. Aucun frais n'est dû en cas d'annulation à l'initiative du prestataire.
        </p>
      </Section>

      <Section title="10. Durée de validité des packs" testId="cg-validite">
        <p>
          Sauf accord écrit contraire, les heures d'un pack sont valables <strong className="text-[#C9A869]">12 mois</strong> à compter du premier cours réalisé. Passé ce délai, les heures non consommées sont perdues.
        </p>
      </Section>

      <Section title="11. Obligations du client — Sécurité" testId="cg-securite">
        <p>
          Le client participe aux cours sous sa propre responsabilité. Il déclare être en bonne santé physique pour pratiquer la danse et s'engage à signaler à {legalConfig.brandName} toute contrainte médicale, blessure, grossesse ou condition particulière avant le début du cours.
        </p>
        <p>
          Le client est tenu de respecter les consignes de sécurité du professeur, en particulier pour les figures de rock acrobatique.
        </p>
      </Section>

      <Section title="12. Droit de rétractation" testId="cg-retractation">
        <p>
          Pour les prestations souscrites à distance par un consommateur, l'article L.221-18 du Code de la consommation prévoit un délai de rétractation de 14 jours.
        </p>
        <p>
          Toutefois, conformément à l'article L.221-25 du Code de la consommation, lorsque la prestation est <strong className="text-[#C9A869]">exécutée intégralement avant la fin du délai de rétractation à la demande expresse du client</strong>, ce dernier renonce expressément à son droit de rétractation pour la partie déjà exécutée. Ce point est rappelé lors de la confirmation de réservation.
        </p>
        <p>
          Le droit de rétractation ne s'applique pas non plus aux prestations programmées à une date précise (article L.221-28 13°).
        </p>
      </Section>

      <Section title="13. Propriété intellectuelle" testId="cg-pi">
        <p>
          Les chorégraphies, supports pédagogiques et contenus créés par {legalConfig.brandName} restent sa propriété exclusive. Toute captation vidéo lors d'un cours pour un usage autre que personnel doit faire l'objet d'un accord écrit préalable.
        </p>
      </Section>

      <Section title="14. Réclamations & médiation" testId="cg-mediation">
        <p>
          Toute réclamation doit être adressée par écrit à&nbsp;:&nbsp;
          <a href={`mailto:${legalConfig.contactEmail}`} className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">{legalConfig.contactEmail}</a>.
          Une réponse est apportée dans un délai de 15 jours ouvrés.
        </p>
        <p>
          Conformément à l'article L.616-1 du Code de la consommation, en cas de litige persistant, le consommateur peut recourir gratuitement à un médiateur de la consommation.
        </p>
        {legalConfig.consumerMediator.designated ? (
          <>
            <Field label="Médiateur désigné" value={legalConfig.consumerMediator.name} />
            <Field label="Adresse" value={legalConfig.consumerMediator.address} />
            <Field label="Site internet" value={legalConfig.consumerMediator.website} />
          </>
        ) : (
          <InfoNote variant="warning">
            {legalConfig.brandName} procède actuellement à l'adhésion auprès d'un médiateur de la consommation agréé. Les coordonnées seront publiées ici dès la finalisation de cette démarche.
          </InfoNote>
        )}
        <p className="mt-3">
          Le consommateur peut également saisir la plateforme européenne de règlement en ligne des litiges&nbsp;: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">ec.europa.eu/consumers/odr</a>.
        </p>
      </Section>

      <Section title="15. Droit applicable et juridiction" testId="cg-droit">
        <p>
          Les présentes CG sont régies par le droit français. À défaut de résolution amiable, tout litige sera porté devant les tribunaux compétents du ressort du domicile du défendeur.
        </p>
      </Section>

      <p className="mt-10 text-sm text-[#F7F3EA]/60">
        Voir aussi :{" "}
        <Link to="/mentions-legales" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">Mentions légales</Link>
        {" · "}
        <Link to="/confidentialite" className="text-[#C9A869] hover:text-[#DCC28E] underline underline-offset-4">Politique de confidentialité</Link>
      </p>
    </LegalLayout>
  );
}
